// @flow

import type { PropsSchema, EasyAction, BasicProps } from '../../type'
import type { Rules } from 'frame/core/formValidate'
import * as React from 'react'
import computeData from '../hoc/computeData'
import { validate } from 'frame/core/formValidate'
import closePng from '../ui/img/close.png'
import { isInIOS } from 'frame/core/browserEnv'
import css from './Form.scss'

type Props = BasicProps & {
  style: { [string]: string | number },
  field: string,
  rules: Rules,
  type?: 'text' | 'number' | 'tel' | 'password' | 'date',
  maxLength?: number,
  decimalLength?: number,
  placeholder: string,
  formValues: { [string]: string },
  customProps: any,
  setFormValues: ({ [key: string]: any }) => void,
  showClear: boolean,
  closeAutoComplete: boolean,
  allNumKeyboard: boolean,
  blurReset: boolean,
  events?: Object
};
type State = {
  isFocus: boolean,
  keyword: string,
  readOnly: boolean
};
@computeData()
export default class InputText extends React.PureComponent<Props, State> {
  state = {
    isFocus: false,
    keyword: '',
    readOnly: false
  };
  static defaultProps = {
    type: 'text',
    closeAutoComplete: false,
    allNumKeyboard: false
  };
  changeTimer: any;
  input: any;

  static schema: PropsSchema = [
    {
      key: 'field',
      title: '字段',
      type: 'text'
    },
    {
      key: 'placeholder',
      title: '占位内容',
      type: 'text'
    },
    {
      key: 'type',
      title: '类型',
      type: 'select',
      options: [
        ['text', '文本'],
        ['number', '数字'],
        ['tel', '电话'],
        ['password', '密码'],
        ['hidden', '隐藏'],
        ['search', '搜索'],
        ['money', '金额'],
        ['date', '日期']
      ],
      defaultValue: 'text'
    },
    {
      key: 'maxLength',
      title: '最大长度',
      type: 'number'
    },
    {
      key: 'decimalLength',
      title: '小数位',
      type: 'number',
      visible: props => props.type === 'money'
    },
    {
      title: '校验规则列表',
      type: 'array',
      key: 'rules',
      item: [
        {
          type: 'select',
          key: 'type',
          title: '规则：',
          options: [
            ['required', '必填'],
            ['mobile', '手机号'],
            ['idCard', '身份证'],
            ['bankCard', '银行卡'],
            ['zh_cn', '汉字'],
            ['number', '数字'],
            ['en_us', '字母'],
            ['regex', '自定义正则表达式']
          ],
          defaultValue: 'required'
        },
        {
          type: 'text',
          key: 'regex',
          title: '正则',
          visible: props => props.type === 'regex'
        },
        {
          type: 'text',
          key: 'message',
          title: '提示消息'
        }
      ]
    },
    'separator',
    {
      type: 'style'
    },
    'separator',
    {
      type: 'boolean',
      key: 'showClear',
      title: '显示清空按钮'
    },
    {
      type: 'boolean',
      key: 'closeAutoComplete',
      title: '关闭自动完成'
    },
    {
      type: 'boolean',
      key: 'allNumKeyboard',
      title: '显示全数字键盘',
      visible: props => props.type === 'number' || props.type === 'money'
    },
    {
      type: 'boolean',
      key: 'blurReset',
      title: '失去焦点复位(仅ios)'
    },
    'separator',
    {
      type: 'event',
      title: '失去焦点',
      event: 'onBlur'
    },
    {
      type: 'event',
      title: '获得焦点',
      event: 'onFocus'
    },
    {
      type: 'event',
      title: '改变',
      event: 'onChange'
    },
    {
      type: 'event',
      title: '延迟改变',
      event: 'onDelayChange'
    },
    {
      type: 'event',
      title: '按下Enter键',
      event: 'onKeyDown'
    },
    {
      type: 'event',
      title: '校验成功',
      event: 'resolve'
    },
    {
      type: 'event',
      title: '校验失败',
      event: 'reject'
    }
  ];

  static actions: EasyAction[] = [
    {
      title: '数据校验',
      name: 'validate'
    },
    {
      title: '只读',
      name: 'readonly'
    },
    {
      title: '可写',
      name: 'writable'
    },
    {
      title: '获得焦点',
      name: 'focus'
    }
  ];

  componentDidUpdate (preProps: Props) {
    let { callActions } = this.props
    if (this.getFieldValue(preProps) !== this.getFieldValue()) {
      if (this.isExistDelayChange()) {
        if (this.changeTimer) {
          clearTimeout(this.changeTimer)
        }
        let value = this.getFieldValue()
        this.changeTimer = setTimeout(() => {
          callActions('onDelayChange', value)
        }, 200)
      }
      callActions('onChange', this.getFieldValue())
    }
  }

  isExistDelayChange () {
    const { events = {} } = this.props
    const { onDelayChange = {} } = events
    const { targets = [] } = onDelayChange
    return targets.length > 0
  }

  getFieldValue = (props?: Props): string => {
    const { field, formValues } = props || this.props
    return formValues[field] == null ? '' : formValues[field]
  };

  actionValidate = () => {
    const { callActions, rules } = this.props
    const fieldValue = this.getFieldValue()
    if (rules && rules.length > 0) {
      const error = validate(fieldValue, rules)
      if (!error.success) {
        callActions('reject', error)
      } else {
        callActions('resolve')
      }
    } else {
      callActions('resolve')
    }
  };

  actionReadonly = () => {
    this.setState({ readOnly: true })
  };

  actionWritable = () => {
    this.setState({ readOnly: false })
  };

  actionFocus = () => {
    if (this.input) {
      let val = this.input.value
      this.input.focus()
      this.input.value = ''
      this.input.value = val
    }
  };

  onChange = (event: SyntheticEvent<HTMLInputElement>) => {
    let { field, setFormValues } = this.props
    const value: string = event.currentTarget.value
    setFormValues({ [field]: value })
  };
  handelBlur = (event: SyntheticEvent<HTMLInputElement>) => {
    const { showClear, callActions, blurReset } = this.props
    if (blurReset && isInIOS() && document.body) {
      setTimeout(() => {
        document.body && (document.body.scrollTop += 1)
      }, 1)
    }
    setTimeout(() => {
      if (showClear) {
        this.setState({ isFocus: false })
      }
      callActions('onBlur', this.getFieldValue())
    }, 200)
  };
  handleFocus = () => {
    this.setState({ isFocus: true })
    this.props.callActions('onFocus', this.getFieldValue())
  };
  handelClick = () => {
    let { field, setFormValues } = this.props
    setFormValues({ [field]: '' })
  };
  onEnterKey = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const { callActions } = this.props
      event.preventDefault()
      callActions('onKeyDown', this.getFieldValue())
    }
  };

  onInput = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    let { type } = this.props
    let value: string = event.currentTarget.value
    if (type === 'money') {
      value = this.moneyData(value)
      event.currentTarget.value = value // 强制更新
    }
  };
  moneyData = (value: string) => {
    let oldValue = this.getFieldValue()
    let { maxLength, decimalLength = '' } = this.props
    maxLength = parseInt(maxLength)
    decimalLength = parseInt(decimalLength)
    let regStr = `^([1-9]\\d*|0)?$`
    if (
      decimalLength > 0 &&
      (!maxLength || (maxLength && maxLength > decimalLength))
    ) {
      regStr = `^([1-9]\\d*|0)?(\\.\\d{0,${decimalLength}})?$`
    }
    if (
      !new RegExp(regStr).test(value) ||
      (maxLength && value.length > maxLength)
    ) {
      value = oldValue
    }
    return value
  };

  render () {
    if (!this.props.visible) {
      return null
    }
    const {
      id,
      style,
      maxLength,
      field,
      jsEnv,
      placeholder,
      showClear,
      closeAutoComplete,
      allNumKeyboard
    } = this.props
    let { type } = this.props
    const value = this.getFieldValue()
    const { isFocus, readOnly } = this.state
    if (type === 'money') {
      type = 'number'
    }
    if (showClear) {
      style.paddingLeft = style.paddingLeft || 0
      return (
        <div
          className={css.placeholderColor}
          style={{ position: 'relative', display: style.display || 'inline' }}
        >
          <input
            name={field}
            maxLength={maxLength}
            type={type}
            placeholder={jsEnv.getString(placeholder)}
            onChange={this.onChange}
            onInput={this.onInput}
            value={value}
            style={{
              ...style,
              paddingRight: '0.45rem'
            }}
            id={id}
            onBlur={this.handelBlur}
            autoComplete={closeAutoComplete ? 'off' : 'on'}
            onFocus={this.handleFocus}
            pattern={allNumKeyboard ? '[0-9]*' : ''}
            readOnly={readOnly}
            ref={ref => (this.input = ref)}
          />
          {!readOnly && isFocus && showClear && value != null && (
            <img
              style={{
                position: 'absolute',
                right: '5px',
                top: '50%',
                width: '0.3rem',
                transform: 'translateY(-50%)'
              }}
              onClick={this.handelClick}
              src={closePng}
            />
          )}
        </div>
      )
    }
    return (
      <input
        name={field}
        maxLength={maxLength}
        type={type}
        placeholder={jsEnv.getString(placeholder)}
        onChange={this.onChange}
        onBlur={this.handelBlur}
        onFocus={this.handleFocus}
        onKeyDown={this.onEnterKey}
        onInput={this.onInput}
        value={this.getFieldValue()}
        style={style}
        autoComplete={closeAutoComplete ? 'off' : 'on'}
        id={id}
        readOnly={readOnly}
        pattern={allNumKeyboard ? '[0-9]*' : ''}
        ref={ref => (this.input = ref)}
      />
    )
  }
}



// WEBPACK FOOTER //
// ./src/projects/easy/public/Comps/form/FormInput.js